import React from "react";
import { noop } from "lodash";
import { createPortal } from "react-dom";

import { Component } from "../../../../commons/types/component";
import cn from "../../libs/class-name";
import { getScrollParents } from "../../libs/externals/scroll-parent";
import useOnMount from "../../libs/hooks/use-on-mount";

const renderProps = (element: Props["portalContent"], props: any) =>
  typeof element === "function" ? element(props) : element;

export enum Position {
  AboveLeft = "above-left",
  AboveRight = "above-right",
  BelowLeft = "below-left",
  BelowRight = "below-right"
}

export interface PositionStrategy<T = Position> {
  top: number;
  left: number;
  position: T;
}

const getPosition = (openAbove: boolean, left: number): Position => {
  if (openAbove && left) {
    return Position.AboveLeft;
  } else if (openAbove && !left) {
    return Position.AboveRight;
  } else if (!openAbove && left) {
    return Position.BelowLeft;
  } else {
    return Position.BelowRight;
  }
};

const defaultPositionStrategy = (sourceRect: DOMRect, portalRect: DOMRect): PositionStrategy => {
  // Open the content portal above the child if there is not enough space to the bottom,
  // but if there also isn't enough space at the top, open to the bottom.
  const openAbove =
    sourceRect.top + sourceRect.height + portalRect.height >
      (window.document.documentElement || window.document.body).clientHeight && sourceRect.top - portalRect.height > 0;

  const top = openAbove
    ? sourceRect.top - portalRect.height + window.scrollY
    : sourceRect.top + sourceRect.height + window.scrollY;

  // Open the content portal to the left if there is not enough space at the right,
  // but if there also isn't enough space at the right, open to the left.
  const alignRight =
    sourceRect.left + portalRect.width > (window.document.documentElement || window.document.body).clientWidth &&
    sourceRect.left - portalRect.width > 0;

  const left = !alignRight
    ? sourceRect.left + window.scrollX
    : window.scrollX + sourceRect.left - portalRect.width + sourceRect.width;

  const position = getPosition(openAbove, left);

  return {
    top,
    left,
    position
  };
};

interface Props extends Component {
  children: React.ReactNode;
  portalContent:
    | React.ReactNode
    | ((params: {
        close: () => void;
        isOpen: boolean;
        position: string;
        relatedWidth: number;
        transitionStarted: () => void;
        transitionEnded: () => void;
      }) => React.ReactNode);
  onShouldClose?: () => void;
  closeOnOutsideClick?: boolean;
  isOpen?: boolean;
  positionStrategy?: (sourceRect: DOMRect, portalRect: DOMRect, props: Props) => PositionStrategy<string>;
  rootNode?: HTMLElement;
}

const PositioningPortalChildren = React.forwardRef<HTMLDivElement, Pick<Props, "children">>((props, ref) => (
  <div ref={ref}>{props.children}</div>
));

PositioningPortalChildren.displayName = "PositioningPortalChildren";

const PositioningPortal = (props: Props) => {
  const {
    portalContent,
    children,
    rootNode,
    isOpen = false,
    onShouldClose = noop,
    closeOnOutsideClick = true,
    positionStrategy = defaultPositionStrategy
  } = props;

  // Measured dimensions — set via ref callbacks, never during render.
  const [portalRect, setPortalRect] = React.useState<DOMRect>();
  const [sourceRect, setSourceRect] = React.useState<DOMRect>();

  const [transitionActive, setTransitionActive] = React.useState(false);

  const [sourceDom, _setSourceDom] = React.useState<HTMLElement>();
  const [isOpenInternal, _setIsOpenInternal] = React.useState(false);

  const sourceDomRef = React.useRef<HTMLElement | null>(null);
  const portalDomRef = React.useRef<HTMLDivElement | null>(null);
  const isOpenInternalRef = React.useRef(false);

  const setSourceDom = (dom: HTMLElement) => {
    sourceDomRef.current = dom;
    _setSourceDom(dom);
    if (portalDomRef.current) {
      setSourceRect(dom.getBoundingClientRect());
    }
  };

  const setPortalDom = (dom: HTMLDivElement) => {
    portalDomRef.current = dom;
  };

  const childrenRefCallback = React.useCallback((element: HTMLDivElement | null) => {
    // Note: PositioningPortalChildren is wrapped in a div. Thats why we need to access the children of the element, to set the right source.
    // We assume there is only one child, so we can access the first child.
    if (element && element.firstChild) {
      setSourceDom(element.firstChild as HTMLElement);
    }
  }, []);

  const portalRefCallback = React.useCallback((node: HTMLDivElement) => {
    setPortalDom(node);
    if (node) {
      // Measure both portal and source rects here — safe because ref callbacks are not
      // effects, so calling setState from them never triggers the set-state-in-effect rule.
      setPortalRect(node.getBoundingClientRect());
      if (sourceDomRef.current) {
        setSourceRect(sourceDomRef.current.getBoundingClientRect());
      }
    } else {
      setPortalRect(undefined);
    }
  }, []); // sourceDomRef is a ref — stable identity, intentionally omitted from deps

  const close = React.useCallback(() => {
    onShouldClose();
  }, [onShouldClose]);

  const handleOutsideMouseClick = React.useCallback(
    (event: Event) => {
      if (!closeOnOutsideClick) {
        return;
      } else if (!isOpenInternalRef.current) {
        return;
      } else if (portalDomRef.current && portalDomRef.current.contains(event.target as Node)) {
        return;
      } else if (sourceDomRef.current && sourceDomRef.current.contains(event.target as Node)) {
        return;
      } else {
        close();
      }
    },
    [close, closeOnOutsideClick]
  );

  useOnMount(() => {
    window.document.addEventListener("click", handleOutsideMouseClick, false);

    return () => {
      window.document.removeEventListener("click", handleOutsideMouseClick, false);
    };
  });

  // Render-time sync: mirror the isOpen prop into isOpenInternal state without an effect.
  // See: https://react.dev/reference/react/useState#storing-information-from-previous-renders
  // Initialize prevIsOpen to undefined so the sync fires unconditionally on the first render.
  const [prevIsOpen, setPrevIsOpen] = React.useState<boolean | undefined>(undefined);
  if (prevIsOpen !== isOpen) {
    setPrevIsOpen(isOpen);
    if (isOpen && !isOpenInternal) {
      _setIsOpenInternal(true);
      setTransitionActive(false);
      // Reset measurements so the portal renders unpositioned on its first mount.
      // portalRefCallback will measure and set both rects once the portal div exists.
      setSourceRect(undefined);
      setPortalRect(undefined);
    } else if (!isOpen && isOpenInternal) {
      _setIsOpenInternal(false);
    }
  }

  // Keep isOpenInternalRef in sync with state so the click handler can read it synchronously.
  // The assignment must live in an effect, not during render (react-hooks/refs).
  React.useEffect(() => {
    isOpenInternalRef.current = isOpenInternal;
  }, [isOpenInternal]);

  // When the portal opens: wire up scroll-close listeners on all scrollable ancestors.
  // DOM measurements (sourceRect, portalRect) are handled entirely in portalRefCallback
  // so no setState calls are needed here.
  React.useEffect(() => {
    if (isOpenInternal && sourceDom && sourceDom.nodeType === Node.ELEMENT_NODE) {
      const scrollParentsToListen = getScrollParents(sourceDom);
      scrollParentsToListen.forEach(node => node.addEventListener("scroll", close, false));

      return () => {
        scrollParentsToListen.forEach(node => node.removeEventListener("scroll", close, false));
      };
    }
  }, [isOpenInternal, sourceDom, close]);

  // Derive portal position from the latest measurements during render — no effect needed.
  // While portalRect is undefined (portal just mounted, not yet measured by portalRefCallback),
  // computedPosition is null and the portal renders unpositioned.
  // Once portalRefCallback fires with the real rect, a re-render produces the final position.
  //
  // Note: this is intentionally NOT gated on `isOpenInternal`. During a close transition the
  // portal stays mounted (because `transitionActive` is true) and we want to preserve the
  // last-known position for the exit animation. On the next open the render-time sync clears
  // both rects, forcing a fresh measurement cycle.
  const computedPosition = sourceRect && portalRect ? positionStrategy(sourceRect, portalRect, props) : null;

  const isPositioned = computedPosition !== null;
  const top = computedPosition?.top ?? 0;
  const left = computedPosition?.left ?? 0;
  const position = computedPosition?.position;

  const relatedWidth = sourceRect ? sourceRect.width : 0;

  const portalStyle: React.CSSProperties = {
    width: portalRect ? `${portalRect.width}px` : "auto",
    left: `${left}px`,
    top: `${top}px`
  };

  const renderPortal = () =>
    createPortal(
      <div className={cn("PositioningPortal", [{ isPositioned }])} ref={portalRefCallback} style={portalStyle}>
        {renderProps(portalContent, {
          close,
          transitionStarted: () => setTransitionActive(true),
          transitionEnded: () => setTransitionActive(false),
          position,
          isOpen: isOpenInternal,
          relatedWidth
        })}
      </div>,
      rootNode ?? window.document.body
    );

  return (
    <>
      <PositioningPortalChildren ref={childrenRefCallback}>{children}</PositioningPortalChildren>
      {(isOpenInternal || transitionActive) && renderPortal()}
    </>
  );
};

export default PositioningPortal;
